import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SearchPickerInput,
  useSearchPicker,
  FormControl,
  FormErrorMessage,
  useBoolean,
} from '../../../../../../design-system/src';
import { Option } from '../../../../../../design-system/src/content/components/SearchPickerList/SearchPickerList';
import { prefilledInputComponentStyling } from '../../../../constants/InputComponentStyling';
import useFlowInputComponent from '../../../../hooks/useFlowsInputComponent';
import useQuestionEnum from '../../../../hooks/useQuestionEnum';
import { Inputs } from '../../../../types/Enums';
import { FlowsQuestion } from '../../../../types/FlowsTypes';
import { getFlowsDataTestId } from '../../../../utils/testUtils';

interface Props {
  question: FlowsQuestion;
}

const MultiselectInputComponent: React.FC<React.PropsWithChildren<Props>> = ({
  question,
}) => {
  const { t } = useTranslation();

  const {
    field: { value, onBlur },
    error,
    prefilled,
  } = useFlowInputComponent({ question });

  const chosenValues = (value as string[] | undefined) || [];

  const enumOptions = useQuestionEnum(question);

  const options = useCallback(
    (val: string): Option[] => {
      return enumOptions.filter((o) =>
        o.label.toLowerCase().includes(val.toLowerCase()),
      );
    },
    [enumOptions],
  );

  // Shitty logic
  const [update, setUpdate] = useBoolean();

  useEffect(() => {
    setUpdate.off();
  }, [update]);

  const { componentProps } = useSearchPicker({
    searchFunction: options,
    update,
  });

  const handleOnBlur = (options: Option[]) => {
    setUpdate.on();

    const newOptions = options.map(({ value }) => value);
    onBlur({
      name: question.id,
      value: newOptions.length ? newOptions : [],
    });
  };

  return (
    <FormControl isInvalid={!!error}>
      <SearchPickerInput
        {...componentProps}
        selectedOptions={enumOptions.filter(({ value: _value }) =>
          chosenValues.includes(_value),
        )}
        setSelectedOptions={handleOnBlur}
        boxShadow="base"
        border="1px"
        borderColor={error?.message ? 'danger.main' : 'gray.50'}
        hideIcons
        withSelectAll
        placeholder={t('DEFAULT_MULTISELECT_OPTIES')}
        {...(prefilled && {
          sx: {
            ...prefilledInputComponentStyling,
            input: {
              bg: '#FFF9EA',
            },
          },
        })}
        data-test-id={getFlowsDataTestId(Inputs.MULTISELECT, question)}
        isDisabled={question.rules.is_disabled}
      />
      <FormErrorMessage justifyContent="flex-end">
        {error?.message}
      </FormErrorMessage>
    </FormControl>
  );
};

export default MultiselectInputComponent;
